import React from 'react'
import PropTypes from 'prop-types'
import {
  compose,
  withState,
  withHandlers,
  withProps,
  defaultProps,
  branch,
  renderComponent,
} from 'recompose'
import find from 'lodash/find'
import styled from 'styled-components'
import { Card } from 'semantic-ui-react'
import Image from 'cms/CmsCaptionImage'
import Name from 'cms/components/productCard/Name'
import Badge from 'cms/components/productCard/Badge'
import Attributes from 'cms/components/productCard/Attributes'
import Brand from 'cms/components/productCard/Brand'
import ProductPrice from 'cms/components/productCard/ProductPrice'
import ProductLoading from 'cms/components/productCard/ProductLoading'
import { createDynamicStyle } from 'cms/helpers/styledComponents'
import { Link } from 'routes'

const CardStyled = styled(Card)`
  ${({ theme }) => ({
    '&.ui.card': {
      ...createDynamicStyle(theme, 'cms', 'productCard', 'card')(),
      '.product-attributes': {
        ...createDynamicStyle(theme, 'cms', 'productCard', 'attributes')(),
      },
      '.product-image': {
        ...createDynamicStyle(theme, 'cms', 'productCard', 'image')(),
        '.product-badge': {
          ...createDynamicStyle(theme, 'cms', 'productCard', 'badge')(),
          ':before': {
            ...createDynamicStyle(theme, 'cms', 'productCard', 'beforeBadge')(),
          },
        },
      },
      '.content': {
        ...createDynamicStyle(theme, 'cms', 'productCard', 'content')(),
        '.product-name': {
          ...createDynamicStyle(theme, 'cms', 'productCard', 'name')(),
        },
        '.product-brand': {
          ...createDynamicStyle(theme, 'cms', 'productCard', 'brand')(),
        },
        '.product-attributes': {
          ...createDynamicStyle(theme, 'cms', 'productCard', 'attributes')(),
        },
        '.product-price': {
          ...createDynamicStyle(theme, 'cms', 'productCard', 'price')(),
          '.static-price': {
            ...createDynamicStyle(theme, 'cms', 'productCard', 'staticPrice')(),
            '&.discount': {
              ...createDynamicStyle(theme, 'cms', 'productCard', 'discount')(),
            },
          },
        },
      },
    },
  })}
`

const getBrand = (properties) => {
  if (!properties.length) {
    return ''
  }
  const brand = find(properties, ({ code }) => code === 'brand')
  return brand ? brand.value : ''
}

const getProductImage = (product = {}, noImageSource) => {
  const { media: { gallery } } = product || {}
  return gallery && gallery.length ? gallery[0].file : noImageSource
}

const withProductCard = compose(
  withState('isHovered', 'setHoverState', false),
  withHandlers({
    onCardHover: ({ setHoverState }) => () => {
      setHoverState(true)
    },
    onCardUnHover: ({ setHoverState }) => () => {
      setHoverState(false)
    },
  }),
  defaultProps({
    product: {},
  }),
  withProps(({
    product,
    defaultImage,
    shouldDisplayBrand,
  }) => {
    const {
      sku = '',
      name = '',
      price = {},
      properties = [],
      media = {},
      children = [],
      attributes = [],
    } = product
    return {
      name,
      price: price.original,
      discount: price.discount,
      // use Link instead
      // onClick: () => (router ? router.pushRoute(`/product/${sku}/${name}`) : {}),
      brand: shouldDisplayBrand ? getBrand(properties) : '',
      image: media.gallery && media.gallery.length ? (
        getProductImage(product, defaultImage)
      ) : (
        children.length ? getProductImage(children[0], defaultImage) : ''
      ),
      attributeData: attributes,
      url: `/product/${sku}/${name}`,
    }
  }),
)

const ProductCard = compose(
  branch(
    ({ loading }) => loading,
    renderComponent(ProductLoading),
  ),
  withProductCard,
)(
  ({
    product,
    name,
    image,
    price,
    discount,
    brand,
    attributeData,
    attributeStyle,
    hasBadge,
    onCardHover,
    onCardUnHover,
    defaultImage,
    attributeRule,
    originalPricePosition,
    isHovered,
    url,
  }) => (
    <Link route={url}>
      <a>
        <div>
          <CardStyled
            as={'a'}
            onMouseEnter={onCardHover}
            onMouseLeave={onCardUnHover}
          >
            <div className="product-image">
              {hasBadge && (
                <Badge product={product} />
              )}
              {attributeStyle === 'overlay' && (
                <Attributes.Overlay
                  show={isHovered}
                  attributeRule={attributeRule}
                  attributeData={attributeData}
                />
              )}
              <Image
                src={image}
                isZoomWhenHovered={false}
                defaultImage={defaultImage}
              />
            </div>
            <Card.Content>
              <Name name={name} />
              {attributeStyle !== 'overlay' && (
                <Attributes
                  attributeRule={attributeRule}
                  attributeData={attributeData}
                />
              )}
              {brand && (
                <Brand brand={brand} />
              )}
              <ProductPrice
                price={price}
                discount={discount}
                originalPricePosition={originalPricePosition}
              />
            </Card.Content>
          </CardStyled>
        </div>
      </a>
    </Link>
  ),
)

ProductCard.propTypes = {
  loading: PropTypes.bool,
  product: PropTypes.object,
  onClick: PropTypes.func,
  hasBadge: PropTypes.bool,
  defaultImage: PropTypes.string,
  attributeRule: PropTypes.array,
  originalPricePosition: PropTypes.oneOf(['top', 'bottom']),
  shouldDisplayBrand: PropTypes.bool,
  isHovered: PropTypes.bool,
}

ProductCard.defaultProps = {
  product: {},
  onClick: () => { },
  hasBadge: false,
  defaultImage: '',
  attributeRule: [],
  attributeStyle: 'normal', // or overlay
  originalPricePosition: 'top',
  shouldDisplayBrand: false,
  loading: false,
  isHovered: false,
}

export default ProductCard



// WEBPACK FOOTER //
// ./src/cms/CmsProductCard.js